<script setup>
/**
 * Photo gallery: a tight, image-forward grid of uniform thumbnails. Each tile
 * is a button that opens the shared PhotoViewer lightbox at that image, where
 * the full image and its caption are shown. Captions are kept out of the grid
 * so the wall stays clean and dense rather than bulky.
 */
const props = defineProps({ data: { type: Object, required: true } });

const images = computed(() => props.data.images.filter(Boolean));

const openIndex = ref(null);
</script>

<template>
  <section v-if="images.length" class="py-10 md:py-14">
    <div class="container-x">
      <div class="grid grid-cols-2 gap-1.5 sm:grid-cols-3 sm:gap-2 lg:grid-cols-4">
        <button
          v-for="(img, i) in images"
          :key="i"
          type="button"
          class="group relative aspect-[4/3] overflow-hidden rounded-lg bg-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-green"
          @click="openIndex = i"
        >
          <img
            :src="img.url"
            :alt="img.alt_text || ''"
            loading="lazy"
            class="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <!-- Hover veil + zoom affordance -->
          <span
            aria-hidden="true"
            class="absolute inset-0 bg-ink/0 transition-colors duration-300 group-hover:bg-ink/25"
          />
          <span
            aria-hidden="true"
            class="absolute bottom-2 right-2 grid h-8 w-8 place-items-center rounded-full bg-paper/90 text-ink opacity-0 shadow-soft transition-opacity duration-300 group-hover:opacity-100"
          >
            <svg class="h-4 w-4" viewBox="0 0 20 20" fill="none">
              <circle cx="9" cy="9" r="6" stroke="currentColor" stroke-width="1.6" />
              <path d="M13.5 13.5L17 17M9 6.5v5M6.5 9h5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" />
            </svg>
          </span>
          <!-- Caption peek on hover (full text lives in the viewer) -->
          <span
            v-if="img.caption"
            class="absolute inset-x-0 bottom-0 translate-y-full bg-gradient-to-t from-ink/80 to-transparent px-3 pb-2 pt-6 text-left text-xs leading-snug text-white transition-transform duration-300 group-hover:translate-y-0"
          >
            <span class="line-clamp-2">{{ img.caption }}</span>
          </span>
        </button>
      </div>
    </div>

    <PhotoViewer v-model="openIndex" :images="images" />
  </section>
</template>
